// Data-driven competitor comparison pages. One entry here renders a full,
// SEO-optimised "Reality Twin vs. X" landing page at /vs/<slug> (plus the
// comparisons hub at /compare). Content is intentionally honest — each page
// says when the competitor is the better pick — which reads as credible and
// ranks better than one-sided marketing.

export type CompareRow = [capability: string, them: string, us: string];

export type Competitor = {
  slug: string;
  name: string;
  category: string; // what they are, in a few words
  metaTitle: string;
  metaDescription: string;
  heroEmphasis: string; // the italic tail of the H1
  intro: string; // hero paragraph
  shortAnswer: string;
  rows: CompareRow[];
  differentiators: { title: string; body: string }[];
  whenThem: string[];
  whenUs: string[];
  migration: string;
  faqs: { q: string; a: string }[];
};

const OUR_ROWS_TAIL: CompareRow[] = [
  ["Company marketplace", "—", "Public marketplace listing included"],
  [
    "Twin-to-Twin discovery",
    "—",
    "Your Twin is reachable by other companies' Twins over an open Agent Card protocol",
  ],
  ["Data residency", "US-hosted by default", "Swiss / EU-first"],
];

export const COMPETITORS: Competitor[] = [
  {
    slug: "chatbase",
    name: "Chatbase",
    category: "Customer-support AI agent",
    metaTitle: "Reality Twin vs. Chatbase: the B2B alternative (2026)",
    metaDescription:
      "Chatbase alternative for B2B: Reality Twin adds a Twin-to-Twin discovery network, a company marketplace, persistent memory and Swiss data residency on top of the AI-agent-on-your-site basics.",
    heroEmphasis: "the B2B alternative",
    intro:
      "Chatbase is a well-built AI agent platform for customer support. Reality Twin solves a different problem: turning your company into an AI Twin that qualifies B2B leads and is reachable by other companies' Twins through a discovery network. Here's an honest comparison so you can pick the right tool.",
    shortAnswer:
      "Chatbase is a mature customer-support agent used by thousands to deflect tickets. Reality Twin is a B2B Twin platform: your website becomes a reasoning agent that qualifies leads, books meetings, and joins a network where other companies' Twins can find and message yours. For a smart FAQ, Chatbase is a fine pick. For revenue, discovery and inter-company workflows, Reality Twin is the category you want.",
    rows: [
      [
        "Primary use case",
        "Ticket deflection on your own site",
        "Lead qualification, meetings, revenue",
      ],
      ["Knowledge ingestion", "Site + docs upload", "Site + docs + re-crawled on a schedule"],
      ["Takes action", "Answers questions", "Books appointments & files paid requests in-chat"],
      ["Memory across sessions", "Per session", "Persistent per visitor / account"],
      ...OUR_ROWS_TAIL,
    ],
    differentiators: [
      {
        title: "Twin-to-Twin discovery is a different product",
        body: "Chatbase lives on your own website — a visitor lands, they chat, that's it. Reality Twin adds a second surface: other companies' Twins can query yours directly for pricing, fit and references, no human involved. A buyer's Twin can evaluate five vendors overnight and surface the two worth a meeting. If your only presence is a chat widget, you're invisible to that flow.",
      },
      {
        title: "It acts, not just answers",
        body: "Support agents match a question to a document and paraphrase. Reality Twin's Twin books a real appointment or files a paid service request straight from the conversation, and hands the qualified lead to a human with context — not just a transcript.",
      },
    ],
    whenThem: [
      "Your #1 goal is deflecting FAQ tickets on a support page.",
      "You don't need CRM-aware qualification or scoring.",
      "You don't care about being reachable by other companies' AI agents.",
    ],
    whenUs: [
      "You sell B2B and every conversation is a potential pipeline entry.",
      "You want your AI to qualify, book and score — not just answer FAQs.",
      "You want your company discoverable by other companies' Twins.",
      "You need Swiss / EU data residency for regulated markets.",
    ],
    migration:
      "There's nothing to hand-port. Point Reality Twin at your website URL and optionally upload the same documents. It re-crawls on a schedule and builds its own knowledge graph — the FAQ editor stops being a job.",
    faqs: [
      {
        q: "Is Reality Twin a Chatbase alternative?",
        a: "Yes — for B2B revenue use cases. Chatbase is strong for support ticket deflection on a single site. Reality Twin qualifies leads, books meetings and is reachable by other companies' Twins across a network.",
      },
      {
        q: "What can Reality Twin do that Chatbase cannot?",
        a: "Twin-to-Twin discovery, in-chat appointment booking and paid service requests, a public company marketplace, persistent memory and Swiss / EU data residency by default.",
      },
      {
        q: "When is Chatbase the right choice instead?",
        a: "If your only goal is deflecting support tickets on a public help page, a mature support-agent platform like Chatbase is a solid pick.",
      },
    ],
  },
  {
    slug: "botpress",
    name: "Botpress",
    category: "Developer chatbot builder",
    metaTitle: "Reality Twin vs. Botpress: no-build B2B AI Twin (2026)",
    metaDescription:
      "Botpress alternative: Reality Twin gives you a reasoning B2B AI Twin with a discovery network and marketplace — without building flows. Honest comparison for 2026.",
    heroEmphasis: "without building flows",
    intro:
      "Botpress is a powerful, flow-based chatbot builder for teams that want to engineer conversations node by node. Reality Twin takes the opposite approach: point it at your website and it becomes a reasoning Twin — no flow diagrams, plus a discovery network Botpress doesn't have.",
    shortAnswer:
      "Botpress is for builders who want deep control over conversational flows and custom code. Reality Twin is for companies that want a competent B2B Twin live in minutes — grounded in their real content, qualifying and booking on its own, and discoverable by other companies' Twins. If you have engineers and want to hand-craft flows, Botpress fits. If you want outcomes without the build, Reality Twin does.",
    rows: [
      ["Setup", "Build flows node by node", "Point at your website — live in minutes"],
      ["Who runs it", "Developers / bot builders", "Founders, marketing, sales"],
      ["Answers", "Scripted + NLU intents", "Grounded reasoning over your content"],
      ["Takes action", "Whatever you build", "Books appointments & files requests out of the box"],
      ...OUR_ROWS_TAIL,
    ],
    differentiators: [
      {
        title: "No flow-building — grounded reasoning instead",
        body: "Botpress asks you to model intents and design the conversation tree. Reality Twin reads your website and documents and reasons from them, so it handles questions you never scripted. You maintain your content, not a flow diagram.",
      },
      {
        title: "Distribution, not just a widget",
        body: "Botpress deploys a bot to your channels. Reality Twin also lists you on a public marketplace and exposes an Agent Card other companies' Twins can find and query — a growth surface a bot builder doesn't provide.",
      },
    ],
    whenThem: [
      "You have developers and want node-level control of flows.",
      "You need a highly custom, scripted conversation for a niche process.",
      "You're embedding a bot deep into a bespoke product.",
    ],
    whenUs: [
      "You want a capable B2B Twin without building or maintaining flows.",
      "You want it to qualify, book and hand off — not just route intents.",
      "You want discovery and a marketplace presence, not only a widget.",
    ],
    migration:
      "No flows to rebuild. Give Reality Twin your website URL and any docs; it constructs its own knowledge graph and starts answering — you skip the flow-authoring entirely.",
    faqs: [
      {
        q: "Is Reality Twin a Botpress alternative?",
        a: "Yes, for teams that want outcomes without engineering flows. Reality Twin is grounded in your content and live in minutes, with a discovery network Botpress doesn't offer.",
      },
      {
        q: "Do I need developers for Reality Twin?",
        a: "No. There are no flows to build. Point it at your site, optionally upload documents, and publish.",
      },
      {
        q: "When is Botpress the better pick?",
        a: "When you have engineers and genuinely need node-level control of a bespoke, scripted conversation.",
      },
    ],
  },
  {
    slug: "intercom",
    name: "Intercom (Fin)",
    category: "Customer messaging suite + AI",
    metaTitle: "Reality Twin vs. Intercom Fin: B2B Twin alternative (2026)",
    metaDescription:
      "Intercom Fin alternative for B2B growth: Reality Twin qualifies leads, books meetings and joins a Twin discovery network — without per-resolution pricing. Honest 2026 comparison.",
    heroEmphasis: "built for pipeline, not tickets",
    intro:
      "Intercom is a mature customer-messaging suite and its Fin AI agent is excellent at support resolution. Reality Twin isn't a helpdesk — it's a B2B Twin that turns website visitors into qualified, booked pipeline, and makes your company discoverable to other companies' Twins.",
    shortAnswer:
      "Intercom + Fin is a support powerhouse priced around ticket resolution. Reality Twin is a growth tool: it qualifies and scores leads, books meetings in-chat and lists you on a discovery network. If support at scale is your problem, Intercom is world-class. If turning traffic into revenue is your problem, Reality Twin is the sharper fit — with far simpler pricing.",
    rows: [
      ["Built for", "Support & customer messaging", "B2B lead-to-pipeline + discovery"],
      ["Pricing model", "Seats + per-resolution", "Flat conversation plans, generous free tier"],
      ["Lead qualification", "Add-on / manual", "Native scoring, notes and CRM webhook"],
      ["Takes action", "Resolves tickets", "Books appointments & files paid requests in-chat"],
      ...OUR_ROWS_TAIL,
    ],
    differentiators: [
      {
        title: "Priced for growth, not per resolution",
        body: "Fin bills around resolved conversations, which is sensible for support but punishes high top-of-funnel volume. Reality Twin uses flat conversation plans and a free tier, so a busy marketing site doesn't turn every visitor question into a line item.",
      },
      {
        title: "A discovery surface a helpdesk doesn't have",
        body: "Intercom keeps your AI inside your own inbox and messenger. Reality Twin also publishes an Agent Card and marketplace listing, so other companies' Twins can find, vet and message yours for partnerships and B2B buying.",
      },
    ],
    whenThem: [
      "Support volume is your primary problem and you want a full inbox suite.",
      "You already run Intercom for messaging and want AI inside it.",
      "Per-resolution pricing matches your economics.",
    ],
    whenUs: [
      "Your website's job is pipeline, and you want AI that qualifies and books.",
      "You want predictable, conversation-based pricing.",
      "You want inter-company discovery, not just a messenger.",
    ],
    migration:
      "Run Reality Twin alongside Intercom: keep Intercom for support and put Reality Twin on your marketing and pricing pages where the job is qualification. Point it at your site — no content migration needed.",
    faqs: [
      {
        q: "Is Reality Twin an Intercom alternative?",
        a: "For the B2B growth job, yes. It qualifies, scores and books leads and adds a discovery network. For pure support-inbox scale, Intercom remains excellent and the two can run side by side.",
      },
      {
        q: "How does pricing compare to Fin?",
        a: "Reality Twin uses flat conversation plans with a free tier, instead of per-resolution billing — friendlier for high-volume top-of-funnel traffic.",
      },
      {
        q: "Can I use both?",
        a: "Yes. Many teams keep Intercom for support and use Reality Twin on revenue pages for qualification and booking.",
      },
    ],
  },
  {
    slug: "drift",
    name: "Drift",
    category: "Conversational marketing / chat",
    metaTitle: "Reality Twin vs. Drift: AI Twin for B2B conversion (2026)",
    metaDescription:
      "Drift alternative: Reality Twin replaces playbook routing with a reasoning Twin that qualifies, books and is discoverable by other companies' Twins. Honest 2026 comparison.",
    heroEmphasis: "reasoning, not playbooks",
    intro:
      "Drift pioneered conversational marketing with routing playbooks and rep handoffs. Reality Twin modernises the idea: instead of maintaining branching playbooks, a grounded Twin reasons from your real content, qualifies and books on its own, and plugs into a discovery network.",
    shortAnswer:
      "Drift is strong at routing known-intent visitors to the right rep via playbooks. Reality Twin removes the playbook maintenance: it reasons from your content, handles unscripted questions, books meetings itself, and adds inter-company discovery. If you love playbook-driven routing, Drift fits. If you want an autonomous Twin that just handles it, Reality Twin does.",
    rows: [
      ["Approach", "Branching playbooks", "Grounded reasoning from your content"],
      ["Maintenance", "Keep playbooks updated", "Maintain your content, not flows"],
      ["Qualification", "Rules + rep handoff", "AI scoring + in-chat booking"],
      ["Reach", "Your site", "Site + Twin discovery network"],
      ...OUR_ROWS_TAIL,
    ],
    differentiators: [
      {
        title: "No playbook treadmill",
        body: "Drift's power is also its overhead — someone owns the playbooks. Reality Twin reasons from your live content, so there's no branching logic to maintain as your offering changes.",
      },
      {
        title: "It books the meeting itself",
        body: "Drift routes to a calendar or a rep. Reality Twin collects the details and books the appointment in-conversation, then notifies your team with full context.",
      },
    ],
    whenThem: [
      "You have a team that loves owning routing playbooks.",
      "Your motion is ABM with tightly scripted, known-account flows.",
    ],
    whenUs: [
      "You want qualification and booking without maintaining flows.",
      "You want a Twin that handles unscripted questions from your content.",
      "You want discovery beyond your own website.",
    ],
    migration:
      "No playbooks to port. Point Reality Twin at your site; it reasons from your content immediately and books meetings without a routing tree.",
    faqs: [
      {
        q: "Is Reality Twin a Drift alternative?",
        a: "Yes — it delivers conversational conversion without playbook maintenance, and adds a discovery network Drift doesn't have.",
      },
      {
        q: "Does it book meetings like Drift?",
        a: "It goes further — the Twin books the appointment itself in the conversation, then notifies your team.",
      },
      {
        q: "When is Drift the better fit?",
        a: "When your motion is heavily playbook- and rep-routing-driven and a team owns that process.",
      },
    ],
  },
  {
    slug: "tidio",
    name: "Tidio",
    category: "SMB live chat + chatbot",
    metaTitle: "Reality Twin vs. Tidio: B2B AI Twin alternative (2026)",
    metaDescription:
      "Tidio alternative for B2B: Reality Twin adds reasoning over your content, in-chat booking, lead scoring and a Twin discovery network beyond simple live chat. Honest 2026 comparison.",
    heroEmphasis: "past live chat, into pipeline",
    intro:
      "Tidio is a friendly live-chat and chatbot tool loved by small businesses and e-commerce. Reality Twin is aimed at B2B: a reasoning Twin that qualifies and books, grounded in your real content, plus a network that makes your company discoverable to others.",
    shortAnswer:
      "Tidio is great, affordable live chat with basic bots for SMB and stores. Reality Twin is a B2B Twin: it reasons from your content, scores and books leads, and joins a discovery network. If you want simple live chat on a shop, Tidio fits. If your site's job is qualifying B2B buyers, Reality Twin is the upgrade.",
    rows: [
      ["Sweet spot", "SMB / e-commerce live chat", "B2B lead-to-pipeline"],
      ["Bot type", "Rule-based flows + basic AI", "Grounded reasoning over your content"],
      ["Qualification", "Basic", "AI scoring, notes, CRM webhook"],
      ["Takes action", "Canned replies / handoff", "Books appointments & files paid requests"],
      ...OUR_ROWS_TAIL,
    ],
    differentiators: [
      {
        title: "Reasoning instead of rule trees",
        body: "Tidio's bots follow rules you configure. Reality Twin reasons from your website and documents, so it fields questions you never anticipated and stays right as your content changes.",
      },
      {
        title: "A B2B growth surface",
        body: "Tidio is a widget on your site. Reality Twin adds a marketplace listing and an Agent Card so other companies' Twins can discover and query yours — distribution a chat widget can't offer.",
      },
    ],
    whenThem: [
      "You run an online store or SMB site and want simple, cheap live chat.",
      "Your questions are mostly order status and basic FAQs.",
    ],
    whenUs: [
      "You sell B2B and need real qualification and booking.",
      "You want reasoning over your content, not rule trees.",
      "You want discovery beyond your own widget.",
    ],
    migration:
      "Point Reality Twin at your site and publish. There are no rule trees to recreate — it builds its own knowledge from your content.",
    faqs: [
      {
        q: "Is Reality Twin a Tidio alternative?",
        a: "For B2B, yes — it reasons from your content, qualifies and books, and adds a discovery network beyond simple live chat.",
      },
      {
        q: "Is it more expensive than Tidio?",
        a: "Reality Twin has a free tier too; it targets B2B revenue rather than SMB live chat, so compare on qualified pipeline, not just chat volume.",
      },
      {
        q: "When is Tidio the better fit?",
        a: "For e-commerce and SMB sites that mainly need affordable live chat and basic bots.",
      },
    ],
  },
  {
    slug: "ada",
    name: "Ada",
    category: "Enterprise support automation",
    metaTitle: "Reality Twin vs. Ada: B2B Twin vs support automation (2026)",
    metaDescription:
      "Ada alternative for B2B growth: Reality Twin qualifies, books and joins a Twin discovery network, where Ada automates enterprise support resolution. Honest 2026 comparison.",
    heroEmphasis: "growth, not deflection",
    intro:
      "Ada is an enterprise-grade support automation platform focused on resolving customer issues at scale. Reality Twin points the same AI energy at the top of the funnel: qualifying B2B buyers, booking meetings, and making your company discoverable to other companies' Twins.",
    shortAnswer:
      "Ada excels at automated enterprise support and deflection. Reality Twin is a growth Twin: it turns visitor conversations into scored, booked pipeline and adds inter-company discovery. Different jobs — deflect tickets vs. create pipeline — and many enterprises want both.",
    rows: [
      ["Focus", "Support deflection at scale", "Lead-to-pipeline + discovery"],
      ["Buyer", "Support / CX leaders", "Marketing, sales, founders"],
      ["Outcome metric", "Deflection / CSAT", "Qualified leads, booked meetings, revenue"],
      ["Takes action", "Resolves & routes tickets", "Books appointments & files paid requests"],
      ...OUR_ROWS_TAIL,
    ],
    differentiators: [
      {
        title: "Top-of-funnel, not the help desk",
        body: "Ada lives in support. Reality Twin lives on your marketing, pricing and product pages, where the job is to qualify and convert — a different outcome than deflection.",
      },
      {
        title: "Inter-company discovery",
        body: "Ada automates your customers' support journeys. Reality Twin also makes your company an addressable Twin other companies can discover and evaluate for partnerships and buying.",
      },
    ],
    whenThem: [
      "Enterprise support deflection is the mandate.",
      "You need deep CX workflows and a large support org.",
    ],
    whenUs: [
      "Your goal is pipeline and revenue from website traffic.",
      "You want qualification, booking and discovery, not deflection.",
    ],
    migration:
      "Run both: Ada for support, Reality Twin on revenue pages. Point Reality Twin at your site — no support content to migrate.",
    faqs: [
      {
        q: "Is Reality Twin an Ada alternative?",
        a: "For the growth job, yes. For enterprise support deflection, Ada is purpose-built; many companies run both for different funnel stages.",
      },
      {
        q: "What does Reality Twin optimise for?",
        a: "Qualified leads, booked meetings and revenue from your website — plus discoverability by other companies' Twins.",
      },
      {
        q: "When is Ada the better fit?",
        a: "When enterprise-scale support automation and deflection is the primary need.",
      },
    ],
  },
  {
    slug: "landbot",
    name: "Landbot",
    category: "No-code chatbot flows",
    metaTitle: "Reality Twin vs. Landbot: reasoning Twin vs flows (2026)",
    metaDescription:
      "Landbot alternative: Reality Twin replaces drag-and-drop flows with a reasoning B2B Twin that qualifies, books and is discoverable by other Twins. Honest 2026 comparison.",
    heroEmphasis: "no flows to drag",
    intro:
      "Landbot makes building chatbot flows visual and fun. Reality Twin skips flow-building entirely: it reads your website and reasons, qualifying and booking on its own, and joins a discovery network no flow builder has.",
    shortAnswer:
      "Landbot is a delightful drag-and-drop flow builder. Reality Twin is a grounded reasoning Twin — no flows to design, it answers from your content, books meetings and is discoverable by other companies' Twins. If you enjoy designing conversational flows, Landbot fits. If you want the outcome without the design work, Reality Twin does.",
    rows: [
      ["Build", "Drag-and-drop flows", "Point at your website"],
      ["Answers", "Scripted branches", "Grounded reasoning from your content"],
      ["Maintenance", "Update flows", "Update content"],
      ["Takes action", "Whatever you wire", "Books appointments & files requests out of the box"],
      ...OUR_ROWS_TAIL,
    ],
    differentiators: [
      {
        title: "Reasoning beats branches",
        body: "Every Landbot answer is a branch someone designed. Reality Twin reasons from your live content, so it handles the long tail of questions no flow covers.",
      },
      {
        title: "Distribution beyond your page",
        body: "Landbot embeds a flow. Reality Twin adds a marketplace listing and Agent Card so other companies' Twins can find and query yours.",
      },
    ],
    whenThem: [
      "You want to hand-design a specific, playful conversational flow.",
      "Your use case is a well-bounded, scripted interaction.",
    ],
    whenUs: [
      "You want qualification and booking without designing flows.",
      "You want reasoning over your content and inter-company discovery.",
    ],
    migration:
      "No flows to rebuild — point Reality Twin at your site and it reasons from your content immediately.",
    faqs: [
      {
        q: "Is Reality Twin a Landbot alternative?",
        a: "Yes — it delivers conversational qualification without designing flows, and adds a discovery network.",
      },
      {
        q: "Do I lose control without flows?",
        a: "You control the content and personality; the Twin reasons from that instead of following branches, covering far more questions.",
      },
      {
        q: "When is Landbot the better fit?",
        a: "When you specifically want to hand-craft a bounded, scripted flow.",
      },
    ],
  },
  {
    slug: "custom-gpt",
    name: "Custom GPTs",
    category: "ChatGPT custom GPTs",
    metaTitle: "Reality Twin vs. a Custom GPT: for B2B websites (2026)",
    metaDescription:
      "Custom GPT alternative for your business: Reality Twin grounds answers in your live site, captures and scores leads, books meetings and joins a Twin network — beyond a GPT locked in ChatGPT. Honest 2026 comparison.",
    heroEmphasis: "off ChatGPT, onto your site",
    intro:
      "A Custom GPT is a fast way to wrap ChatGPT around some instructions and files. Reality Twin is a product: a Twin embedded on your own site, grounded in your live content, that captures leads, books meetings and is discoverable by other companies' Twins.",
    shortAnswer:
      "A Custom GPT lives inside ChatGPT, answers from files you upload, and can't capture a lead or book a meeting on your website. Reality Twin embeds on your site, re-crawls your content, scores and stores leads, books appointments and joins a discovery network. For a personal helper, a Custom GPT is great. For a business surface, Reality Twin is built for it.",
    rows: [
      ["Where it lives", "Inside ChatGPT", "Embedded on your website + marketplace"],
      ["Knowledge freshness", "Static uploaded files", "Re-crawled from your live site"],
      ["Captures leads", "—", "Scores, stores and notifies + CRM webhook"],
      ["Takes action", "—", "Books appointments & files paid requests"],
      ...OUR_ROWS_TAIL,
    ],
    differentiators: [
      {
        title: "It's on your site and it converts",
        body: "A Custom GPT sits behind ChatGPT's login and can't put a lead in your CRM. Reality Twin runs on your own domain, captures the visitor's details, scores intent and notifies your team — a growth tool, not a sandbox.",
      },
      {
        title: "Fresh knowledge, no re-uploading",
        body: "Custom GPT files go stale the moment your pricing changes. Reality Twin re-crawls your site on a schedule, so the Twin is always current without you re-uploading anything.",
      },
    ],
    whenThem: [
      "You want a quick personal assistant inside ChatGPT.",
      "You don't need lead capture, embedding or booking.",
    ],
    whenUs: [
      "You want the Twin on your website, capturing and booking real leads.",
      "You want always-fresh knowledge and CRM handoff.",
      "You want discovery by other companies' Twins.",
    ],
    migration:
      "Reuse the same instructions and files you gave your Custom GPT — then point Reality Twin at your website so it stays fresh and actually captures leads.",
    faqs: [
      {
        q: "Why not just use a Custom GPT?",
        a: "A Custom GPT can't live on your website, capture a lead, book a meeting or stay fresh as your content changes. Reality Twin does all four and adds a discovery network.",
      },
      {
        q: "Can I reuse my GPT's instructions?",
        a: "Yes — paste them as your Twin's personality and upload the same files; then connect your website for always-fresh knowledge.",
      },
      {
        q: "When is a Custom GPT enough?",
        a: "For a personal helper inside ChatGPT with no need for embedding, lead capture or booking.",
      },
    ],
  },
  {
    slug: "crisp",
    name: "Crisp",
    category: "SMB shared inbox + chatbot",
    metaTitle: "Reality Twin vs. Crisp: B2B AI Twin alternative (2026)",
    metaDescription:
      "Crisp alternative for B2B: Reality Twin adds grounded reasoning, in-chat booking, lead scoring and a Twin discovery network beyond a shared inbox and bots. Honest 2026 comparison.",
    heroEmphasis: "an agent, not an inbox",
    intro:
      "Crisp bundles a shared inbox, live chat and simple bots at a friendly price. Reality Twin isn't an inbox — it's a reasoning Twin that qualifies and books B2B leads from your content and joins a discovery network.",
    shortAnswer:
      "Crisp is a tidy, affordable messaging suite with basic bots. Reality Twin is a B2B growth Twin: it reasons from your real content, scores and books leads, and is discoverable by other companies' Twins. For an all-in-one SMB inbox, Crisp is good value. For qualifying B2B buyers, Reality Twin is the sharper tool.",
    rows: [
      ["Core", "Shared inbox + live chat", "Reasoning B2B Twin"],
      ["Bot type", "Scenario bots", "Grounded reasoning over your content"],
      ["Qualification", "Basic", "AI scoring, notes, CRM webhook"],
      ["Takes action", "Canned scenarios / handoff", "Books appointments & files paid requests"],
      ...OUR_ROWS_TAIL,
    ],
    differentiators: [
      {
        title: "Reasoning, not scenarios",
        body: "Crisp's bots run scenarios you script. Reality Twin reasons from your website and documents, so it answers the questions you didn't anticipate and stays right as your content changes.",
      },
      {
        title: "A discovery surface, not just an inbox",
        body: "Crisp centralises your conversations. Reality Twin also publishes a marketplace listing and Agent Card so other companies' Twins can find and query yours.",
      },
    ],
    whenThem: [
      "You want an affordable all-in-one inbox for a small team.",
      "Your bots only need simple, scripted scenarios.",
    ],
    whenUs: [
      "Your site's job is qualifying and booking B2B buyers.",
      "You want reasoning over your content plus inter-company discovery.",
    ],
    migration:
      "Keep Crisp for your inbox if you like; put Reality Twin on the pages where qualification matters. Point it at your site — no scenarios to rebuild.",
    faqs: [
      {
        q: "Is Reality Twin a Crisp alternative?",
        a: "For B2B qualification and booking, yes — it reasons from your content and adds a discovery network. For a pure SMB shared inbox, Crisp remains good value.",
      },
      {
        q: "Can I run both?",
        a: "Yes — many teams keep Crisp for support conversations and use Reality Twin on revenue pages.",
      },
      {
        q: "When is Crisp the better fit?",
        a: "When you mainly want an affordable all-in-one inbox with simple scenario bots.",
      },
    ],
  },
  {
    slug: "voiceflow",
    name: "Voiceflow",
    category: "Conversational AI design platform",
    metaTitle: "Reality Twin vs. Voiceflow: build-free B2B AI Twin (2026)",
    metaDescription:
      "Voiceflow alternative: Reality Twin gives you a grounded B2B Twin with a discovery network — without designing and maintaining agents. Honest 2026 comparison.",
    heroEmphasis: "no agent to design",
    intro:
      "Voiceflow is a powerful canvas for teams that want to design and orchestrate conversational agents in detail. Reality Twin is the opposite bet: point it at your website and it becomes a competent B2B Twin — no canvas, plus a discovery network.",
    shortAnswer:
      "Voiceflow gives design and ops teams deep control to build custom agents. Reality Twin gives companies a working B2B Twin in minutes, grounded in their content, qualifying and booking on its own, and discoverable by other Twins. If you want to design agents, Voiceflow fits. If you want the outcome without building, Reality Twin does.",
    rows: [
      ["Setup", "Design agents on a canvas", "Point at your website"],
      ["Who runs it", "Conversation designers / devs", "Founders, marketing, sales"],
      ["Answers", "What you design + NLU", "Grounded reasoning over your content"],
      ["Takes action", "Whatever you build", "Books appointments & files requests out of the box"],
      ...OUR_ROWS_TAIL,
    ],
    differentiators: [
      {
        title: "Outcome without the build",
        body: "Voiceflow's strength is granular design — and its cost is that someone designs and maintains the agent. Reality Twin reads your content and reasons, so there's no canvas to build or keep current.",
      },
      {
        title: "Distribution built in",
        body: "Voiceflow ships an agent to your channels. Reality Twin also lists you on a marketplace and exposes an Agent Card other companies' Twins can query — a growth surface a design tool doesn't provide.",
      },
    ],
    whenThem: [
      "You have a team that wants to design bespoke agents in detail.",
      "You need fine-grained control over a complex, custom flow.",
    ],
    whenUs: [
      "You want a capable B2B Twin without designing or maintaining agents.",
      "You want reasoning over your content and inter-company discovery.",
    ],
    migration:
      "Nothing to rebuild — give Reality Twin your website URL and it constructs its own knowledge and starts qualifying, skipping the design canvas entirely.",
    faqs: [
      {
        q: "Is Reality Twin a Voiceflow alternative?",
        a: "For teams that want a working B2B Twin without designing agents, yes — and it adds a discovery network Voiceflow doesn't have.",
      },
      {
        q: "Do I need conversation designers?",
        a: "No. Point Reality Twin at your site; it reasons from your content instead of a designed flow.",
      },
      {
        q: "When is Voiceflow the better fit?",
        a: "When you specifically want to design and orchestrate bespoke agents with fine-grained control.",
      },
    ],
  },
  {
    slug: "hubspot",
    name: "HubSpot Chatflows",
    category: "CRM chat + Breeze AI",
    metaTitle: "Reality Twin vs. HubSpot Chatflows: B2B AI Twin (2026)",
    metaDescription:
      "HubSpot Chatflows alternative: Reality Twin adds grounded reasoning, in-chat booking and a Twin discovery network — and works even if you don't live in HubSpot. Honest 2026 comparison.",
    heroEmphasis: "reasoning, not routing rules",
    intro:
      "HubSpot's Chatflows and Breeze bots are convenient if your whole go-to-market already lives in HubSpot. Reality Twin is a standalone B2B Twin that reasons from your content, books meetings, and adds a discovery network — and it fits whatever CRM you run via webhook.",
    shortAnswer:
      "HubSpot Chatflows shine when you're all-in on HubSpot and want chat wired to that CRM. Reality Twin is a best-of-breed Twin: it reasons from your content, qualifies and books, and pushes leads to any CRM via webhook, plus inter-company discovery. If HubSpot is your everything, its native chat is convenient. If you want the strongest Twin regardless of CRM, Reality Twin.",
    rows: [
      ["Best when", "You live entirely in HubSpot", "Any stack — CRM-agnostic via webhook"],
      ["Bot logic", "Rules + routing (Breeze adds AI)", "Grounded reasoning over your content"],
      ["Takes action", "Routes / books via HubSpot", "Books appointments & files paid requests"],
      ["Lead sync", "Native to HubSpot", "Webhook to HubSpot or any CRM"],
      ...OUR_ROWS_TAIL,
    ],
    differentiators: [
      {
        title: "Best-of-breed, not lock-in",
        body: "HubSpot chat is a feature of a suite you're expected to standardise on. Reality Twin is a focused Twin that reasons better from your content and pushes leads to whatever CRM you use — no all-in commitment.",
      },
      {
        title: "A discovery network HubSpot doesn't have",
        body: "HubSpot keeps your chat inside your funnel. Reality Twin also makes your company an addressable Twin other companies can discover and query for partnerships and buying.",
      },
    ],
    whenThem: [
      "Your entire go-to-market already runs on HubSpot.",
      "You want chat natively wired to HubSpot with minimal setup.",
    ],
    whenUs: [
      "You want the strongest Twin regardless of which CRM you use.",
      "You want reasoning, in-chat booking and inter-company discovery.",
    ],
    migration:
      "Point Reality Twin at your site and connect the lead webhook to HubSpot (or any CRM). You get a stronger Twin without leaving your current stack.",
    faqs: [
      {
        q: "Is Reality Twin a HubSpot Chatflows alternative?",
        a: "Yes — a best-of-breed Twin that reasons from your content and syncs leads to HubSpot or any CRM via webhook, with a discovery network on top.",
      },
      {
        q: "Does it integrate with HubSpot?",
        a: "Leads captured by the Twin can be POSTed to HubSpot (or any CRM) via the lead webhook, with full context.",
      },
      {
        q: "When is HubSpot's native chat the better fit?",
        a: "When your whole team already lives in HubSpot and you want the tightest native wiring with minimal setup.",
      },
    ],
  },
  {
    slug: "zendesk",
    name: "Zendesk",
    category: "Enterprise support suite + AI",
    metaTitle: "Reality Twin vs. Zendesk: B2B growth Twin vs support (2026)",
    metaDescription:
      "Zendesk alternative for B2B growth: Reality Twin qualifies, books and joins a Twin discovery network, where Zendesk automates enterprise support. Honest 2026 comparison.",
    heroEmphasis: "pipeline, not tickets",
    intro:
      "Zendesk is a category-defining support suite, and its AI agents resolve tickets at enterprise scale. Reality Twin points that AI energy at the top of the funnel: qualifying B2B buyers, booking meetings, and making your company discoverable to other companies' Twins.",
    shortAnswer:
      "Zendesk is built for support operations and deflection at scale. Reality Twin is built for revenue: it turns visitor conversations into scored, booked pipeline and adds inter-company discovery. Different jobs — resolve tickets vs. create pipeline — and larger companies often want both.",
    rows: [
      ["Built for", "Support operations & deflection", "B2B lead-to-pipeline + discovery"],
      ["Buyer", "Support / CX leaders", "Marketing, sales, founders"],
      ["Outcome metric", "Deflection, CSAT, SLA", "Qualified leads, booked meetings, revenue"],
      ["Takes action", "Resolves & routes tickets", "Books appointments & files paid requests"],
      ...OUR_ROWS_TAIL,
    ],
    differentiators: [
      {
        title: "Top-of-funnel, not the help desk",
        body: "Zendesk lives in support operations. Reality Twin lives on your marketing, pricing and product pages, where the job is to qualify and convert — a different outcome than ticket deflection.",
      },
      {
        title: "Inter-company discovery",
        body: "Zendesk automates your customers' support journeys. Reality Twin also makes your company an addressable Twin others can discover and vet for partnerships and buying.",
      },
    ],
    whenThem: [
      "Enterprise support operations and deflection are the mandate.",
      "You need deep ticketing, SLAs and a large support org.",
    ],
    whenUs: [
      "Your goal is pipeline and revenue from website traffic.",
      "You want qualification, booking and discovery, not deflection.",
    ],
    migration:
      "Run both: Zendesk for support, Reality Twin on revenue pages. Point Reality Twin at your site — there's no support content to migrate.",
    faqs: [
      {
        q: "Is Reality Twin a Zendesk alternative?",
        a: "For the B2B growth job, yes. For enterprise support operations, Zendesk is purpose-built; many companies run both across different funnel stages.",
      },
      {
        q: "What does Reality Twin optimise for?",
        a: "Qualified leads, booked meetings and revenue from your website — plus discoverability by other companies' Twins.",
      },
      {
        q: "When is Zendesk the better fit?",
        a: "When enterprise-scale support operations, ticketing and deflection are the primary need.",
      },
    ],
  },
];

export const COMPETITOR_BY_SLUG: Record<string, Competitor> = Object.fromEntries(
  COMPETITORS.map((c) => [c.slug, c]),
);
